import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService, ModalType } from 'cl-common-angular';
import { combineLatest } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PaymentMethodType } from '../core/constants/lookups';
import { CampaignInformation } from '../core/data/campaign-information';
import { CampaignLimits } from '../core/data/campaign-limits';
import { CampaignService } from '../core/data/campaign-service';
import { PatternConstants } from '../core/data/pattern-constants';
import { CreditCard } from '../core/data/payment/credit-card';
import { Donor } from '../core/data/payment/donor';
import { Payment } from '../core/data/payment/payment';
import { PaymentConfig } from '../core/data/payment/payment-config';
import { PaymentMethod } from '../core/data/payment/payment-method';
import { PaymentService } from '../core/data/payment/payment.service';
import { SettingsService } from '../core/data/settings.service';
import { MakePaymentModalResultEnum } from './make-payment-modal-result-type';
import { MakePaymentModalResult } from './make-payment-modal.result';

export type AnimationState = 'visible' | 'hidden';

/*
 * Credit-card IIN (Issuer Identification Number)
 * https://baymard.com/checkout-usability/credit-card-patterns
 */
export class IIN {
    start: number;
    end: number;

    constructor(start: number, end: number) {
        this.start = start;
        this.end = end;
    }
}

/*
 * A Credit-card pattern that we use both for masking and validation.  Also defines the IIN range
 * that corresponds to the pattern.  For example, numbers starting with 51-55 map to the pattern
 * for mastercard.
 */
export class CreditCardPattern {
    iins: IIN[];
    pattern: RegExp;
    minLength: number;

    constructor(iins: IIN[] | number, pattern: RegExp, minLength = 0) {
        if (typeof (iins) === 'number') {
            this.iins = [];
            this.iins.push(new IIN(iins, iins));
        } else
            this.iins = iins as IIN[];

        this.pattern = pattern;
        this.minLength = minLength;
    }
}

/*
 * Verifies the credit-card number meets basic length requirements.
 */
export function creditCardLengthValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const length = control.value.replace(/\D/g, '').length;
        const invalidCard = length < 8 || length > 19;
        return invalidCard ? { invalidCard: { value: control.value } } : null;
    };
}

/*
 * Verifies the terms and conditions were accepted.
 */
export function termsAndConditinsValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        return !control.value ? { notAccepted: { value: control.value } } : null;
    };
}

/*
 * Expiration validator
 */
export function expirationValidator(): ValidatorFn {
    return (fg: UntypedFormGroup): { [key: string]: any } | null => {
        const year = fg.get('year');
        const month = fg.get('month');

        if (!year || !month) {
            return null;
        }

        const now = new Date();
        const thisMonth = new Date(now.getFullYear(), now.getMonth());

        const baseYear = Math.round((thisMonth.getFullYear() / 100)) * 100;

        const dt = new Date(baseYear + parseInt(year?.value, 10), parseInt(month?.value, 10) - 1);

        return dt < thisMonth ? { expired: { value: dt } } : null;
    };
}


@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ss-gift-checkout-modal-component',
    templateUrl: './gift-checkout-modal.component.html',
    styleUrls: ['./gift-checkout-modal.component.css'],
    // Nice animations when the modal opens and closes.
    animations: [
        trigger('sidepanel', [
            transition('void => *', [
                style({ position: 'fixed', right: '-320px', opacity: 0.1 }),
                animate(500)
            ]),
            transition('* => void', [
                animate(300, style({ position: 'fixed', right: '-320px', opacity: 0.1 }))
            ])
        ])
    ]
})
export class GiftCheckoutModalComponent {
    private _show: boolean;

    get show(): boolean {
        return this._show;
    }

    /*
    * To support animations when the modal opens and closes, we have to tag the same element where the *ngIf
    * is applied.  So that we can encapsulate this behavior, this means we don't require the caller to
    * understand and apply the tag, but instead take an approach of having the modal control its own *ngIf,
    * and the caller using the show flag to show and hide the modal, as appropriate.
    */
    @Input() set show(value: boolean) {
        if (!this._show && value && this.campaignLimits) {

            if (this.maxAmount < this.minAmount) {
                // Our software tries not to advertise a campaign if its cap has been reached, but even so we
                // handle this situation in case it does happen.  We just show a simple modal to thank the
                // user for their generosity and to let them know the campaign target has already been reached.

                const formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                });

                const msg = `This campaign has already reached its goal of ${formatter.format(this.capAmount)} thanks to the generosity of people like yourself.  Thank you so much for your willingness to help!`;

                this.modalService.activate(ModalType.dialog, msg, 'Campaign Limit Reached');

                setTimeout(() => this.closed.emit(), 1);

                return;
            }
        }

        this._show = value;

        if (this._show) {
            // Whenever the gift-checkout modal is shown, we generate a new idempotent key right then and there,
            // which remains in force until the modal closes, or until we get an error back from the provider
            // suggesting the user retry a new charge.  This way, if the user ends up submitting multiple times
            // for some reason, it won't end up resulting in multiple charges.  Initially, the idempotent key is
            // blank, but we populate it with the call to get the new id, below.
            this.idempotentKey = '';

            // Unfortunately, we can't rely on HTML5 autofocus because of the way the modal is managed in angular.
            // So as a workaround, we use javascript to focus on the amount input whenever the modal is shown.
            // Notice also that we do this after a short delay.  This is to account for the fact that we have an
            // *ngIf tied to the Show input, and the element isn't in the DOM at this moment, but will be on the
            // next cycle.
            setTimeout(() => document.getElementById('amount-input')?.focus(), 1);

            // Always reset the entire form group, for security.
            if (this.formGroup) {
                this.initFormGroup(true);
            }

            this.submitClicked = false;
            this.showMakePaymentModal = false;
            this.makePaymentComplete = false;

            // Get the new idempotent key that will remain in force as long as this modal is active.
            // Please note we disable the spinner to ensure this happens in the background.
            const s = this.paymentService.getNewId(true)
                .subscribe(id => { this.idempotentKey = id; }, undefined, () => { s.unsubscribe(); });
        }
    }

    @Input() campaignInfo: CampaignInformation;

    @Output() closed = new EventEmitter();

    // The formGroup we use for input collection and validation.  See the constructor for how this is setup.
    formGroup: UntypedFormGroup;

    // The credit-card patterns we use for validation and masking of user input, to help
    // them enter the correct card number.
    creditCardPatterns: CreditCardPattern[];

    // The default pattern we use when the IIN doesn't match any of the standard values.
    defaultCreditCardPattern: CreditCardPattern;

    amountInputName = 'amountInput';

    get amountInput() { return this.formGroup.get(this.amountInputName); }
    get privateGift() { return this.formGroup.get('privateGift'); }
    get firstName() { return this.formGroup.get('contactInfo.firstName'); }
    get lastName() { return this.formGroup.get('contactInfo.lastName'); }
    get email() { return this.formGroup.get('contactInfo.email'); }
    get nameOnCard() { return this.formGroup.get('paymentDetails.nameOnCard'); }
    get creditCardNumber() { return this.formGroup.get('paymentDetails.creditCardNumber'); }
    get month() { return this.formGroup.get('paymentDetails.month'); }
    get year() { return this.formGroup.get('paymentDetails.year'); }
    get cvv() { return this.formGroup.get('paymentDetails.cvv'); }
    get zipCode() { return this.formGroup.get('paymentDetails.zipCode'); }
    get termsAndConditions() { return this.formGroup.get('termsAndConditions'); }
    get paymentDetails() { return this.formGroup.get('paymentDetails'); }
    current2DigitYear: number;
    minCvv = 3;
    maxCvv = 4;
    submitClicked = false;
    campaignLimits: CampaignLimits;
    paymentConfig: PaymentConfig | null;
    idempotentKey: string;
    giftConfirmedPrefix = '';
    payment: Payment;
    showMakePaymentModal = false;
    makePaymentComplete = false;

    vm$ = combineLatest([
        this.campaignService.getCampaignLimits(this.campaignId),
        this.paymentService.paymentConfig,
    ]).pipe(
        tap(
            ([campaignLimits, paymentConfig]) => {
                this.campaignLimits = campaignLimits;
                this.initFormGroup();
                this.paymentConfig = paymentConfig;
            }
        ),
    );

    constructor(
        private campaignService: CampaignService,
        private route: ActivatedRoute,
        private router: Router,
        private paymentService: PaymentService,
        private modalService: ModalService,
        private settingsService: SettingsService
    ) {

        this.current2DigitYear = (new Date()).getFullYear() % 100;

        // Setup our credit-card patterns.  Source:
        // https://baymard.com/checkout-usability/credit-card-patterns

        const fourFours = /^(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,4})(\d*)$/g;
        const fourFourFourThree = /^(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,3})(\d*)$/g;
        const fourFourFive = /^(\d{0,4})(\d{0,4})(\d{0,5})(\d*)$/g;
        const fourSixFive = /^(\d{0,4})(\d{0,6})(\d{0,5})(\d*)$/g;
        const fourSixFour = /^(\d{0,4})(\d{0,6})(\d{0,4})(\d*)$/g;
        const sixThirteen = /^(\d{0,6})(\d{0,13})(\d*)$/g;
        const fourFiveSix = /^(\d{0,4})(\d{0,5})(\d{0,6})(\d*)$/g;

        const maestroIins = [new IIN(500000, 509999), new IIN(560000, 589999), new IIN(600000, 699999)];

        this.creditCardPatterns = [
            this.defaultCreditCardPattern = new CreditCardPattern(4, fourFours), // VISA
            new CreditCardPattern(34, fourSixFive), // Amex
            new CreditCardPattern(37, fourSixFive), // Amex
            new CreditCardPattern(62, fourFours, 0),  // China UnionPay
            new CreditCardPattern(62, sixThirteen, 19), // China UnionPay
            new CreditCardPattern(2, fourFours), // MasterCard
            new CreditCardPattern([new IIN(51, 55)], fourFours), // MasterCard
            new CreditCardPattern(maestroIins, fourFourFive, 0), // Maestro 13-digit
            new CreditCardPattern(maestroIins, fourSixFive, 15), // Maestro 15-digit
            new CreditCardPattern(maestroIins, fourFours, 16), // Maestro 16-digit
            new CreditCardPattern(maestroIins, fourFourFourThree, 19), // Maestro 19-digit
            new CreditCardPattern([new IIN(300, 305), new IIN(309, 309), new IIN(36, 36),
            new IIN(38, 39)], fourSixFour), // Diners Club International
            new CreditCardPattern([new IIN(54, 55)], fourFours), // Diners Club USA
            new CreditCardPattern([new IIN(6011, 6011), new IIN(622126, 622925), new IIN(644, 649),
            new IIN(65, 65)], fourFours), // Discover
            new CreditCardPattern([new IIN(3528, 3589)], fourFours), // JCB
            new CreditCardPattern(1, fourFiveSix), // UATP
            new CreditCardPattern(16, fourFours), // Dankort
            new CreditCardPattern(636, fourFours), // InterPayment
        ];
    }

    get campaignId(): number {
        return parseInt(this.route.snapshot.params.id, 10);
    }

    initFormGroup(force = false) {
        if (this.formGroup && !force) {

            // As some of our validators are dependent on our observables, we update validators
            // whenever changes are detected.

            this.updateValidators();

            return;
        }

        this.formGroup = new UntypedFormGroup({
            amountInput: new UntypedFormControl(''),
            privateGift: new UntypedFormControl(false),
            contactInfo: new UntypedFormGroup({
                firstName: new UntypedFormControl('', [Validators.required, Validators.maxLength(100)]),
                lastName: new UntypedFormControl('', [Validators.required, Validators.maxLength(100)]),
                email: new UntypedFormControl('', [Validators.required, Validators.maxLength(255), Validators.pattern(PatternConstants.email)]),
            }),

            paymentDetails: new UntypedFormGroup({
                nameOnCard: new UntypedFormControl('', [Validators.required]),
                creditCardNumber: new UntypedFormControl('',
                    [Validators.required, creditCardLengthValidator()]
                ),
                month: new UntypedFormControl('', [Validators.required, Validators.minLength(1),
                Validators.min(1), Validators.max(12), Validators.pattern(/[0-9]{1,2}/)]
                ),
                year: new UntypedFormControl('', [Validators.required, Validators.minLength(2),
                Validators.min(this.current2DigitYear), Validators.pattern(/[0-9]{2}/)]
                ),
                cvv: new UntypedFormControl('', [Validators.required, Validators.minLength(this.minCvv),
                Validators.maxLength(this.maxCvv), Validators.pattern(/[0-9]{3,4}/)]
                ),
                zipCode: new UntypedFormControl('', [Validators.required, Validators.minLength(5),
                Validators.maxLength(10), Validators.pattern(/^\s*?\d{5}(?:[-\s]\d{4})?\s*?$/)]
                )
            }, expirationValidator()),
            termsAndConditions: new UntypedFormControl('', [Validators.required, termsAndConditinsValidator()])
        });

        this.updateValidators();
    }

    updateValidators() {
        let patternValidator: ValidatorFn | null = null;

        if (this.paymentConfig?.restrictToWholeDollars) {
            patternValidator = Validators.pattern(/^\d+$/);
        } else {
            patternValidator = Validators.pattern(/^\d+(\.\d{0,2})?$/);
        }

        this.amountInput?.setValidators([
            Validators.required, Validators.min(this.minAmount), Validators.max(this.maxAmount),
            patternValidator]);
    }

    get fees(): number {
        return this.campaignLimits.isPercentFee ? (this.campaignLimits.fee / 100) * this.amount : this.campaignLimits.fee;
    }

    get minAmount(): number {
        return this.campaignLimits?.minGiftAmount;
    }

    get capAmount(): number {
        return this.campaignLimits?.capAmount;
    }

    get totalRaised(): number {
        return this.campaignLimits?.totalRaised;
    }

    get maxAmount(): number {
        let retval = 0;

        if (this.capAmount > 0) {
            // Round to the nearest hundredth.
            retval = Math.round((this.capAmount - this.totalRaised) * 100) / 100;
        } else {
            retval = 0;
        }

        if (retval < 0) {
            // We should never show the gift checkout modal if the campaign cap has been reached
            // or exceeded.  This is a fail-safe.  A max a of zero will prevent any further donations.
            retval = 0;
        }

        return retval;
    }

    get amount(): number {
        let amountNum = parseFloat(this.amountInput?.value);

        if (isNaN(amountNum)) {
            amountNum = 0;
        }

        return amountNum;
    }

    get total(): number {
        return this.amount > 0 ? this.amount + this.fees : 0;
    }

    /*
     * Event handler we use to restrict the amount to be in proper format.
     * @param event
     */
    onKeyPressAmount(event: KeyboardEvent) {
        if (this.paymentConfig === null || this.paymentConfig.restrictToWholeDollars) {
            this.onKeyPressDigitOnly(event);
        } else {
            this.onKeyPressNumberOnly(event);
        }
    }

    /*
     * Event handler we use to restrict keystrokes for fields that accept numbers only,
     * including decimals.
     * @param event
     */
    onKeyPressNumberOnly(event: KeyboardEvent) {

        // Get the current value of the input.
        let str = ((event.currentTarget as any).value as string);

        // Now, let's see if any text is selected in the input, which will be overwritten by
        // the new key.

        const elements = document.querySelectorAll(`[formControlName="${this.amountInputName}"]`);

        if (elements && elements.length === 1) {
            const el = elements.item(0) as any;

            if (el.selectionStart >= 0) {
                str = str.substr(0, el.selectionStart) + event.key + str.substr(el.selectionEnd);
            }
            else {
                str = str + event.key;
            }
        }
        const value = Number(str);

        if (isNaN(value)) {
            event.preventDefault();
        } else {
            const idx = str.indexOf('.');

            if (idx >= 0 && str.length - idx > 3) {
                event.preventDefault();
            }
        }
    }

    /*
     * Event handler we use to restrict keystrokes for fields that accept digits only.
     * @param event
     */
    onKeyPressDigitOnly(event: KeyboardEvent) {
        if (Number.isNaN(Number.parseInt(event.key, 10))) {
            event.preventDefault();
        }
    }

    /*
     * Event handler we use to restrict keystrokes for credit cards.
     * @param event
     */
    onKeyPressCreditCard(event: KeyboardEvent) {
        const char = event.key;
        if (Number.isNaN(Number.parseInt(event.key, 10)) && char !== ' ') {
            event.preventDefault();
        }

        // Don't allow more than 19 digits
        if (((event.target as any).value as string).replace(/\D/g, '').length >= 19) {
            event.preventDefault();
        }
    }

    /*
     * Event handler we use to restrict keystrokes for the zip code.
     * @param event
     */
    onKeyPressZipCode(event: KeyboardEvent) {
        const char = event.key;

        const value = (event.target as any).value as string;
        if (value && value.length === 5) {
            if (char !== '-') {
                event.preventDefault();
            }
        }
        else {
            if (Number.isNaN(Number.parseInt(char, 10))) {
                event.preventDefault();
            }
        }
    }

    /*
     * Formats the credit card in real time as the user types, to help them see the patterns
     * and (hopefully) make it easier to enter the correct number.
     * @param event
     */
    onCreditCardChange(event: KeyboardEvent) {
        const value = ((event.target as any).value as string);
        const valueStripped = value.replace(/\D/g, '');

        for (const pattern of this.creditCardPatterns) {

            for (const iin of pattern.iins) {

                const str = iin.start.toString();

                if (valueStripped.length >= str.length) {
                    const sub = valueStripped.substring(0, str.length);
                    const num = Number.parseInt(sub, 10);

                    if (num >= iin.start && num <= iin.end && valueStripped.length >= pattern.minLength) {
                        // We have a match folks.

                        this.applyCreditCardMask(pattern, value, event);
                        return;
                    }
                }
            }
        }

        // We failed to match on the IIN.  Let's revert to the default pattern.
        this.applyCreditCardMask(this.defaultCreditCardPattern, value, event);
    }

    /*
     * Allows enter to be used to toggle the checkbox.
     * @param e
     */
    onKeyPressCheckbox(e: KeyboardEvent) {
        if (e.key === 'Enter') {
            e.preventDefault();

            this.termsAndConditions?.setValue(!this.termsAndConditions?.value);
        }
    }

    applyCreditCardMask(pattern: CreditCardPattern, value: string, event: KeyboardEvent) {
        // First, analyze the regular expression to determine how many groups there
        // are.  This way, we can dynamically build a list of group parts to format
        // the number how we would like.

        const numParts = pattern.pattern.toString().split('(\\d').length - 1;
        let parts = '';

        for (let m = 1; m <= numParts; m++) {
            if (parts.length > 0)
                parts += '  ';

            parts += '$' + m.toString();
        }

        // Now, format the number so it's more readable and matches the layout of the credit card.

        // First, grab any spaces that are on the end.  We will re-include these spaces in our final mask.
        // This way, we let the user type spaces on the end to match what they see on their credit card.
        let spaces = '';

        for (let s = value.length - 1; s >= 0; s--) {
            if (value[s] !== ' ') {
                break;
            }
            spaces += ' ';
        }

        (event.target as any).value =
            // Strip out all spaces
            value.replace(/\D/g, '')
                // Format the credit card to match the pattern
                .replace(pattern.pattern, parts).trim()
            // Finally, add on any spaces at the end the user had typed
            + spaces;
    }

    submit() {
        this.submitClicked = true;

        if (!this.formGroup.valid) {
            // Can't submit.  We show validation in this case.
            this.formGroup.markAllAsTouched();
            return;
        }

        // If the user miraculously filled out the entire form before we could successfully
        // get the new idempotent key in the background, my hat is off to him or her.  Unfortunately,
        // it also means we can't continue.
        if (!this.idempotentKey || !this.idempotentKey.length) {
            // This error is so rare, that we don't really need to show a modal, but can just
            // throw an exception.
            throw new Error('The idempotentKey has not been acquired');
        }

        const payment = new Payment();
        payment.idempotentKey = this.idempotentKey;
        payment.amount = this.amount;
        payment.fee = this.fees;
        payment.campaignId = this.campaignId;
        payment.donor = new Donor(this.firstName?.value?.trim(), this.lastName?.value?.trim(), this.email?.value?.trim(),
            this.zipCode?.value?.trim());
        payment.isPrivate = this.privateGift?.value;

        const paymentMethod = new PaymentMethod();

        // For now, the only payment method we support is credit card
        paymentMethod.paymentMethodType = PaymentMethodType.CreditCard;
        paymentMethod.creditCard = new CreditCard(
            this.nameOnCard?.value?.trim(), this.email?.value?.trim(),
            this.creditCardNumber?.value?.trim()?.replace(/\s+/g, ''),
            this.cvv?.value?.trim(),
            this.month?.value?.trim(), this.year?.value?.trim(), this.zipCode?.value?.trim()
        );

        payment.paymentMethod = paymentMethod;

        // We delegate the actual payment to the make-payment modal.

        this.payment = payment;
        this.showMakePaymentModal = true;
    }

    makePaymentModalClosed(result: MakePaymentModalResult) {
        this.showMakePaymentModal = false;

        if (result.campaignLimits) {
            this.campaignService.updateCampaignLimits(this.payment.campaignId, result.campaignLimits);

            // Rerun validation on the amount now that campaign cap may have changed.
            this.amountInput?.updateValueAndValidity();
        }

        // Update the idempotentKey to a new, unique value, so the next retry is treated as a brand-new request.
        this.idempotentKey = result.newIdempotentKey;

        let allowRetry = false;

        switch (result.result) {
            case MakePaymentModalResultEnum.Success:
                this.giftConfirmedPrefix = 'Gift confirmed.  ';
                break;
            case MakePaymentModalResultEnum.FailureAllowRetry:
                allowRetry = true;
                break;
            case MakePaymentModalResultEnum.FailureNoRetry:
                this.giftConfirmedPrefix = '';
                break;
        }

        if (!allowRetry) {
            this.makePaymentComplete = true;

            const elements = document.getElementsByClassName('sidepanel');

            if (elements && elements.length) {
                // When the payment completes, we commandeer the side panel to show a success message.
                // This message is considerably shorter than the original contents, and so vertically
                // the side panel now looks very small.  To overcome this, we change the height from
                // "auto" to "100%", which will fill the space vertically.

                // Why not just do 100% always and be done with it?  Because that would lead to a
                // second scrollbar in the case where standard gift checkout exceeds the vertical
                // space, which it often does.  So "auto" is what we want when displaying the
                // gift-checkout stuff.

                // Incidentally, this bit of code wouldn't be needed at all if css allowed us to
                // change a parent class based on the presence of a child class.
                (elements[0] as any).style.height = '100%';
                window.scrollTo(0, 0);
            }

            setTimeout(() => {
                document.getElementById('closebtn')?.focus();
            }, 1);
        }
    }

    readMoreStoriesClick() {
        this.closeModal();
        this.router.navigate(['/gallery']);
    }

    closeModal() {
        this.show = false;
        this.closed.emit();
    }
}
